import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import '../styles/NPSChart.css';

/**
 * LLMPositionChart — average list position over time, one line per brand.
 *
 * Position is a rank, so the y-axis is reversed: 1 (named first) sits at the
 * top and larger numbers fall away below. There are no 0-100 "good/bad" bands
 * to paint, so this does not reuse NPSChart.
 */
const LLMPositionChart = ({
    chartData,
    onPointClick,
    penalized = true,
    yAxisTitle = 'Average Position in Answer (1 = named first)',
    xAxisTitle = 'Time Period',
}) => {
    const chartRef = useRef(null);

    const displayChartData = chartData || {
        labels: ['No Data'],
        datasets: [{ label: 'No Chart Data Available', data: [0], borderColor: 'gray', backgroundColor: 'lightgray' }],
    };

    // Give the reversed axis a little headroom past the worst plotted point so
    // the lowest line isn't glued to the bottom edge.
    let worst = 1;
    displayChartData.datasets?.forEach(ds => {
        ds.data?.forEach(pt => {
            const y = typeof pt === 'object' ? pt?.y : pt;
            if (typeof y === 'number' && isFinite(y) && y > worst) worst = y;
        });
    });

    const config = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: true },
        animation: false,
        onClick: (event) => {
            const chart = chartRef.current;
            if (!chart) return;
            const elements = chart.getElementsAtEventForMode(event.native, 'nearest', { intersect: true }, true);
            if (!elements.length || !onPointClick) return;
            const { datasetIndex, index } = elements[0];
            onPointClick({
                label: displayChartData.labels[index],
                datasetIndex,
                index,
                pointData: displayChartData.datasets[datasetIndex].data[index],
            });
        },
        scales: {
            y: {
                reverse: true,          // rank 1 at the top
                min: 1,
                suggestedMax: Math.ceil(worst + 0.5),
                ticks: { precision: 1 },
                title: { display: true, text: yAxisTitle, font: { size: 14, weight: 'bold' } },
            },
            x: {
                title: { display: true, text: xAxisTitle, font: { size: 14, weight: 'bold' } },
            },
        },
        plugins: {
            title: { display: false },
            legend: {
                display: true,
                position: 'top',
                align: 'end',
                labels: { usePointStyle: true, pointStyle: 'circle', padding: 15, boxWidth: 10, boxHeight: 10 },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        if (value == null) return null;
                        const pt = context.dataset.data[context.dataIndex];
                        const lines = [`${context.dataset.label}: ${value.toFixed(2)}`];

                        // Appearance rate is the other half of the story: a brand
                        // can rank well whenever it shows up yet rarely show up.
                        const app = pt?.windowAppearances;
                        const opp = pt?.windowOpportunities;
                        if (opp > 0) {
                            lines.push(`Appeared in ${app} of ${opp} answers (${((100 * app) / opp).toFixed(0)}%)`);
                        }
                        if (penalized && pt?.positionPresent != null) {
                            lines.push(`Position when present: ${Number(pt.positionPresent).toFixed(2)}`);
                        }
                        return lines;
                    },
                },
            },
        },
    };

    return (
        <div className="chart-wrapper">
            <Line ref={chartRef} data={displayChartData} options={config} />
        </div>
    );
};

export default LLMPositionChart;
